import { TranslateService } from '@ngx-translate/core';
import { WidgetSimpleInfo } from '../../campaign/widgets.model';
import { CampaignStatus } from '../../campaign/campaign.model';

const MAX_LISTED_CAMPAIGNS = 5;

const CAMPAIGN_STATUSES = new Set<string>(Object.values(CampaignStatus));

export type UsageLoadState = 'loading' | 'loaded' | 'failed';

export interface AudienceUsage {
  campaigns: readonly WidgetSimpleInfo[];
  usedOnOtherAccounts: boolean;
  loadState: UsageLoadState;
}

/** One widget per campaign, per audience. Counts every status, since a draft loses its targeting too. */
export function campaignsByAudience(widgets: readonly WidgetSimpleInfo[]): Map<string, WidgetSimpleInfo[]> {
  const byAudience = new Map<string, WidgetSimpleInfo[]>();
  for (const widget of widgets) {
    if (!widget.audienceId || widget.campaignId == null) {
      continue;
    }
    const campaigns = byAudience.get(widget.audienceId) ?? [];
    if (!campaigns.some((c) => c.campaignId === widget.campaignId)) {
      campaigns.push(widget);
      byAudience.set(widget.audienceId, campaigns);
    }
  }
  return byAudience;
}

export function campaignDisplayName(widget: WidgetSimpleInfo, translate: TranslateService): string {
  return widget.campaignName || widget.dynamicName || translate.instant('campaign.unnamed');
}

export function campaignStatusLabel(
  status: CampaignStatus | undefined,
  translate: TranslateService,
): string | undefined {
  return status && CAMPAIGN_STATUSES.has(status) ? translate.instant(`campaign.status-${status}`) : undefined;
}

export function audienceDeleteBlockedReason(usage: AudienceUsage, translate: TranslateService): string | null {
  if (usage.campaigns.length > 0) {
    return audienceInUseMessage(usage.campaigns, translate);
  }
  if (usage.usedOnOtherAccounts) {
    return translate.instant('data-platform.audience-delete-in-use-other-accounts');
  }
  switch (usage.loadState) {
    case 'loading':
      return translate.instant('data-platform.audience-delete-checking');
    case 'failed':
      return translate.instant('data-platform.audience-delete-check-failed');
    default:
      return null;
  }
}

function audienceInUseMessage(campaigns: readonly WidgetSimpleInfo[], translate: TranslateService): string {
  const lines = campaigns.slice(0, MAX_LISTED_CAMPAIGNS).map((widget) => {
    const status = campaignStatusLabel(widget.campaignStatus, translate);
    const name = campaignDisplayName(widget, translate);
    return status ? `• ${name} (${status})` : `• ${name}`;
  });
  const hidden = campaigns.length - lines.length;
  if (hidden > 0) {
    lines.push(translate.instant('data-platform.audience-delete-more-campaigns', { count: hidden }));
  }
  return translate.instant('data-platform.audience-delete-in-use', { campaigns: lines.join('\n') });
}
